import * as React from "react"
import { configured, supabase } from "../lib/supabase"
import { ADMINS_LIVE_TABLE } from "../config/project"
import { affiliateFor } from "../lib/affiliate"

/**
 * Who is signed in — MTC-FCS-008.
 * ============================================================================
 * One provider at the root, so every screen reads the same session and none of
 * them fetches it again.
 *
 * ── `ready` IS NOT THE SAME AS `signed out` ────────────────────────────────
 * Restoring a session from storage is asynchronous, so for the first moment
 * after load there is no user AND no answer yet. Screens that conflate the two
 * flash a "sign in" prompt at somebody who is already signed in, or bounce
 * them out of a protected page and back again. `ready` is what separates
 * "nobody is signed in" from "we do not know yet".
 *
 * ── THE SUBSCRIPTION IS THE SOURCE OF TRUTH ────────────────────────────────
 * `onAuthStateChange` covers sign-in, sign-out, token refresh and a session
 * changing in ANOTHER TAB. Reading the session once on mount would leave a
 * second tab showing a signed-in header long after the first tab logged out.
 *
 * ── `isAdmin` IS A SIGN, NOT A LOCK ────────────────────────────────────────
 * It decides whether to SHOW the way into the admin screens (FCS-090 existed
 * for a while with no link to it from anywhere, so an administrator could not
 * find it). Every admin function still checks the `admins` table on the server;
 * a wrong answer here can only show or hide a link, never open anything. So a
 * failed lookup is simply "no link", and nobody is locked out by it.
 */

const AuthContext = React.createContext({
  user: null,
  session: null,
  ready: false,
  available: false,
  isAdmin: false,
  isAffiliate: false,
  affiliate: null,
  rolesReady: false,
  signOut: async () => {},
})

export const AuthProvider = ({ children }) => {
  const [session, setSession] = React.useState(null)
  const [ready, setReady] = React.useState(false)
  // Which user the answer is FOR, so a slow lookup for the previous person
  // can never be read as the current one's.
  const [roles, setRoles] = React.useState({ id: null, admin: false, affiliate: null })

  React.useEffect(() => {
    const client = supabase()
    if (!client) {
      // Nothing to wait for: an unconfigured environment is "ready, nobody
      // signed in", not a permanent loading state.
      setReady(true)
      return undefined
    }

    let cancelled = false

    client.auth.getSession().then(({ data }) => {
      if (cancelled) return
      setSession(data.session ?? null)
      setReady(true)
    })

    const { data: sub } = client.auth.onAuthStateChange((_event, next) => {
      if (cancelled) return
      setSession(next ?? null)
      setReady(true)
    })

    return () => {
      cancelled = true
      sub?.subscription?.unsubscribe()
    }
  }, [])

  const userId = session?.user?.id ?? null
  React.useEffect(() => {
    const client = supabase()
    if (!client || !userId) return undefined
    let cancelled = false
    // RLS lets a signed-in person read only their own rows, so a row means yes.
    Promise.all([
      client.from(ADMINS_LIVE_TABLE).select("id").eq("id", userId).maybeSingle(),
      affiliateFor({ id: userId }),
    ])
      .then(([a, f]) => {
        if (!cancelled) setRoles({ id: userId, admin: !a.error && Boolean(a.data), affiliate: f.affiliate || null })
      })
      .catch(() => {
        if (!cancelled) setRoles({ id: userId, admin: false, affiliate: null })
      })
    return () => {
      cancelled = true
    }
  }, [userId])

  const signOut = React.useCallback(async () => {
    const client = supabase()
    if (client) await client.auth.signOut()
    setSession(null)
  }, [])

  const value = React.useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      ready,
      available: configured(),
      isAdmin: Boolean(userId) && roles.id === userId && roles.admin,
      /* An affiliate has a different account screen from a contractor (FCS-110), so
         "not looked up yet" must be told apart from "not one": rolesReady. */
      isAffiliate: Boolean(userId) && roles.id === userId && Boolean(roles.affiliate),
      affiliate: userId && roles.id === userId ? roles.affiliate : null,
      rolesReady: !userId || roles.id === userId,
      signOut,
    }),
    [session, ready, signOut, userId, roles]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  return React.useContext(AuthContext)
}

/**
 * The access token to send with an API call, or null.
 *
 * Read fresh rather than from React state: a token held in a render can be
 * minutes stale, and the whole point of sending one is that the server can
 * trust it.
 */
export async function accessToken() {
  const client = supabase()
  if (!client) return null
  const { data } = await client.auth.getSession()
  return data.session?.access_token ?? null
}
